import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Copy, CaretDown, CaretUp, PencilSimple, FloppyDisk, Leaf, Egg, Carrot, Drop, Orange, Sparkle, FilePdf, Warning, NotePencil, GearSix, ClockCounterClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ImportBabyMenuDialog } from '@/components/ImportDialogs';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDay, fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const BABY_MENU_PHASES = [
  { key: 'fase_1', label: 'Fase 1 (início da introdução até aos 9 meses)' },
  { key: 'fase_2', label: 'Fase 2 (9 a 12 meses)' },
  { key: 'fase_3', label: 'Fase 3 (12+ meses)' },
];
const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const DAY_LABELS = { segunda: 'Seg', terca: 'Ter', quarta: 'Qua', quinta: 'Qui', sexta: 'Sex', sabado: 'Sáb', domingo: 'Dom' };
const DEFAULT_MEAL_TYPES = [
  { key: 'pequeno_almoco', label: 'Pequeno-almoço' },
  { key: 'meio_manha', label: 'Meio da manhã' },
  { key: 'almoco', label: 'Almoço' },
  { key: 'meio_tarde', label: 'Meio da tarde' },
  { key: 'jantar', label: 'Jantar' },
  { key: 'ceia', label: 'Ceia' },
];
const MENU_RULE_MEALS = ['pequeno_almoco', 'meio_manha', 'almoco', 'lanche', 'jantar', 'ceia'];
const MENU_RULE_MEAL_LABELS = { pequeno_almoco: 'Pequeno-almoço', meio_manha: 'Meio da manhã', almoco: 'Almoço', lanche: 'Lanche', jantar: 'Jantar', ceia: 'Ceia' };
const MENU_RULE_LOCATION_LABELS = { casa: 'Casa', escola: 'Escola', leite: 'Leite' };
const BLANK_ROUTINE = () => Object.fromEntries(DAYS.map(d => [d, Object.fromEntries(MENU_RULE_MEALS.map(m => [m, '']))]));
const BLANK_FISH = () => Object.fromEntries(DAYS.map(d => [d, false]));
const GROUP_ICONS = { energia: Leaf, ferro_proteina: Egg, horticolas: Carrot, gordura: Drop, micronutrientes: Orange };
const GROUP_COLORS = {
  energia: 'border-amber-300 bg-amber-50 text-amber-800',
  ferro_proteina: 'border-red-300 bg-red-50 text-red-800',
  horticolas: 'border-green-300 bg-green-50 text-green-800',
  gordura: 'border-yellow-300 bg-yellow-50 text-yellow-800',
  micronutrientes: 'border-orange-300 bg-orange-50 text-orange-800',
};

function checkAllergenExposureOrder(weeks) {
  // Parte 1.4/1.6 — procura, nas semanas com data de início conhecida, exposições de
  // alergénios (1ª, 2ª, 3ª) cuja ordem cronológica real não seja crescente.
  const exposures = {};
  const sortedWeeks = [...(weeks || [])].filter(w => w.start_date).sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
  sortedWeeks.forEach(w => {
    const order = Array.isArray(w.days_ordered) && w.days_ordered.length === 7 ? w.days_ordered : null;
    if (!order) return;
    const weekLabel = w.label || `S${w.week_number}`;
    order.forEach(d => {
      (d.items || []).forEach(meal => {
        (meal.info_lines || []).forEach(line => {
          const m = line.match(/^Introdução de (.+?): .+\((\d)ª exposição\)$/);
          if (m && d.date) {
            const food = m[1];
            const num = parseInt(m[2], 10);
            exposures[food] = exposures[food] || [];
            exposures[food].push({ num, date: d.date, weekLabel });
          }
        });
      });
    });
  });
  const problems = [];
  Object.entries(exposures).forEach(([food, list]) => {
    const sorted = [...list].sort((a, b) => a.num - b.num);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].date <= sorted[i - 1].date) {
        problems.push({ food, weeks: [...new Set(sorted.map(e => e.weekLabel))] });
        break;
      }
    }
  });
  return problems;
}

function checkHorticolaRepeatViolations(weeks, babyFoods) {
  // Prompt B ponto 3 — hortícola repetido em dias cronológicos consecutivos (incluindo
  // passagem de semana), nas semanas já guardadas (rascunho ou publicadas).
  const horticolaNames = new Set(
    (babyFoods || [])
      .filter(f => f.group === 'horticolas' || (f.extra_groups || []).includes('horticolas'))
      .map(f => (f.name || '').trim().toLowerCase())
  );
  if (horticolaNames.size === 0) return [];
  const sortedWeeks = [...(weeks || [])].filter(w => w.start_date && Array.isArray(w.days_ordered) && w.days_ordered.length === 7)
    .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
  const dayFoods = [];
  sortedWeeks.forEach(w => {
    const weekLabel = w.label || `S${w.week_number}`;
    w.days_ordered.forEach(d => {
      const names = new Set();
      (d.items || []).forEach(meal => (meal.items || []).forEach(it => {
        const n = (it.food || '').trim().toLowerCase();
        if (horticolaNames.has(n)) names.add(n);
      }));
      dayFoods.push({ date: d.date, weekLabel, names });
    });
  });
  const problems = [];
  for (let i = 1; i < dayFoods.length; i++) {
    const prev = dayFoods[i - 1], cur = dayFoods[i];
    if (!prev.date || !cur.date) continue;
    cur.names.forEach(n => {
      if (prev.names.has(n)) {
        problems.push({ food: n, dates: [prev.date, cur.date], weeks: [...new Set([prev.weekLabel, cur.weekLabel])] });
      }
    });
  }
  return problems;
}

function getAlreadyIntroducedMap(weeks) {
  // Parte 1 (ponto 3) — para avisar se se tentar adicionar à lista "Alergénios a introduzir
  // e ordem" um alimento que já teve pelo menos 1 exposição registada noutra semana.
  const map = {};
  (weeks || []).forEach(w => {
    const order = Array.isArray(w.days_ordered) && w.days_ordered.length === 7 ? w.days_ordered : null;
    if (!order) return;
    const weekLabel = w.label || `S${w.week_number}`;
    order.forEach(d => {
      (d.items || []).forEach(meal => {
        (meal.info_lines || []).forEach(line => {
          const m = line.match(/^Introdução de (.+?): .+\((\d)ª exposição\)$/);
          if (m) {
            const food = m[1].trim().toLowerCase();
            if (!map[food]) map[food] = weekLabel;
          }
        });
      });
    });
  });
  return map;
}

function MealEditor({ meal, babyFoods, groupLabels, groupData, onSave, onCancel, mealTypes }) {
  const [type, setType] = useState(meal?.type || 'almoco');
  const [selectedGroups, setSelectedGroups] = useState(() => {
    const base = meal?.groups || [];
    const itemGroups = (meal?.items || []).map(i => i.group).filter(Boolean);
    return Array.from(new Set([...base, ...itemGroups]));
  });
  const [items, setItems] = useState(meal?.items || []);
  const [tip, setTip] = useState(meal?.tip || '');
  const [note, setNote] = useState(meal?.note || '');
  const [infoLines, setInfoLines] = useState(meal?.info_lines || []);
  const [newInfoLine, setNewInfoLine] = useState('');
  const [warnings, setWarnings] = useState([]);
  const currentMonth = new Date().getMonth() + 1;

  // Rule 2: Check group balance on almoco/jantar
  const REQUIRED_GROUPS_MAIN = ['energia', 'ferro_proteina', 'micronutrientes', 'gordura'];
  const GROUP_LABELS_PT = { energia: 'Energia', ferro_proteina: 'Ferro / Proteína', micronutrientes: 'Micronutrientes (Vitamina C)', gordura: 'Gordura' };

  const validateAndSave = () => {
    const newWarnings = [];
    // Rule 2: Group balance for almoco/jantar
    if (type === 'almoco' || type === 'jantar') {
      const missing = REQUIRED_GROUPS_MAIN.filter(g => !selectedGroups.includes(g));
      if (missing.length > 0) {
        const missingLabels = missing.map(g => GROUP_LABELS_PT[g] || groupLabels[g] || g);
        newWarnings.push(`Esta refeição não tem alimento${missing.length > 1 ? 's' : ''} do grupo${missing.length > 1 ? 's' : ''}: ${missingLabels.join(', ')}.`);
      }
    }
    setWarnings(newWarnings);
    // Always allow save (warnings are non-blocking)
    onSave({ type, groups: selectedGroups, items, tip, note, info_lines: infoLines });
  };

  const toggleGroup = (g) => {
    setSelectedGroups(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  const setFoodForGroup = (group, foodName) => {
    setItems(prev => {
      const inGroup = prev.filter(i => i.group === group);
      if (inGroup.length > 1) {
        if (!foodName || prev.some(i => i.group === group && i.food === foodName)) return prev;
        return [...prev, { group, food: foodName }];
      }
      const filtered = prev.filter(i => i.group !== group);
      if (foodName) filtered.push({ group, food: foodName });
      return filtered;
    });
  };

  const foodsForGroup = (group) => {
    return babyFoods.filter(f => {
      if (f.group !== group) return false;
      if (!Array.isArray(f.months) || f.months.length === 0) return true;
      return f.months.includes(currentMonth);
    });
  };

  return (
    <div className="space-y-3" data-testid="meal-editor">
      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Tipo de refeição</Label>
        <div className="flex flex-wrap gap-1.5">
          {mealTypes.map(mt => (
            <button key={mt.key} type="button" onClick={() => setType(mt.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${type === mt.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
              {mt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Alimentos desta refeição</Label>
        {items.length === 0 && <span className="text-[10px] text-brand-placeholder">Nenhum alimento</span>}
        <div className="space-y-1">
          {items.map((it, idx) => (
            <div key={`${it.food}-${idx}`} className="flex items-center gap-1.5 bg-brand-bg/60 rounded-lg px-2 py-1" data-testid={`meal-editor-item-${idx}`}>
              <span className="text-xs text-brand-text flex-1">{it.food}</span>
              <span className="text-[9px] text-brand-muted">{groupLabels[it.group] || it.group}</span>
              <button type="button" onClick={() => setItems(prev => prev.filter((_, j) => j !== idx))} className="text-brand-muted hover:text-red-500 p-0.5" data-testid={`meal-editor-remove-item-${idx}`}><Trash size={11} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Grupos de alimentos</Label>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(groupLabels).filter(([g]) => {
            // Filter by meal type: show group if it has no meal_types (neutral) or if it matches current meal type
            const gd = groupData?.[g];
            if (!gd || !gd.meal_types || gd.meal_types.length === 0) return true;
            // Map meal type keys between mealTypes and MEAL_TYPE_OPTIONS formats
            const typeAliases = { pequeno_almoco: ['pequeno_almoco'], meio_manha: ['lanche_manha', 'meio_manha'], almoco: ['almoco'], meio_tarde: ['lanche_tarde', 'meio_tarde'], jantar: ['jantar'], ceia: ['ceia'] };
            const aliases = typeAliases[type] || [type];
            return gd.meal_types.some(mt => aliases.includes(mt) || mt === type);
          }).map(([g, label]) => {
            const Icon = GROUP_ICONS[g] || Leaf;
            const active = selectedGroups.includes(g);
            return (
              <button key={g} type="button" onClick={() => toggleGroup(g)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${active ? GROUP_COLORS[g] || 'bg-brand-green text-white' : 'bg-white text-brand-muted border-brand-border'}`}
                data-testid={`group-toggle-${g}`}>
                <Icon size={12} weight="duotone" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {selectedGroups.length > 0 && (
        <div className="space-y-2">
          {selectedGroups.map(g => {
            const available = foodsForGroup(g);
            const selected = items.find(i => i.group === g)?.food || '';
            return (
              <div key={g} className={`rounded-xl border p-3 ${GROUP_COLORS[g] || ''}`}>
                <Label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block">{groupLabels[g]}</Label>
                <select value={selected} onChange={e => setFoodForGroup(g, e.target.value)}
                  className="w-full bg-white border border-brand-border rounded-lg px-2 py-1.5 text-sm"
                  data-testid={`food-select-${g}`}>
                  <option value="">Escolher alimento...</option>
                  {available.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      )}

      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Dica de preparo</Label>
        <Input value={tip} onChange={e => setTip(e.target.value)} placeholder="Ex: Triturar tudo junto, textura cremosa" className="rounded-xl border-brand-border text-sm" />
      </div>
      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Nota nutricional</Label>
        <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Ex: Rico em ferro, ideal para esta fase" className="rounded-xl border-brand-border text-sm" />
      </div>

      {/* Info lines */}
      <div>
        <Label className="text-xs text-brand-muted mb-1 block">Linhas de informação</Label>
        {infoLines.map((line, i) => (
          <div key={i} className="flex items-center gap-1.5 mb-1">
            <span className="text-xs text-brand-text flex-1 bg-brand-bg/50 rounded-lg px-2 py-1">{line}</span>
            <button type="button" onClick={() => setInfoLines(prev => prev.filter((_, j) => j !== i))} className="text-brand-muted hover:text-red-500"><Trash size={11} /></button>
          </div>
        ))}
        <div className="flex gap-1.5">
          <Input value={newInfoLine} onChange={e => setNewInfoLine(e.target.value)} placeholder="Ex: introduzir de manhã" className="rounded-lg border-brand-border text-xs h-8 flex-1"
            onKeyDown={e => { if (e.key === 'Enter' && newInfoLine.trim()) { e.preventDefault(); setInfoLines(prev => [...prev, newInfoLine.trim()]); setNewInfoLine(''); } }} />
          <button type="button" onClick={() => { if (newInfoLine.trim()) { setInfoLines(prev => [...prev, newInfoLine.trim()]); setNewInfoLine(''); } }} className="bg-brand-bg text-brand-green rounded-lg px-2 h-8 text-xs border border-brand-border"><Plus size={12} /></button>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-1.5">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
              <Warning size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">{w}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={validateAndSave} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="save-meal-btn">Guardar refeição</button>
        <button onClick={onCancel} className="px-4 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Cancelar</button>
      </div>
    </div>
  );
}

function WeekView({ week, babyFoods, groupLabels, groupData, introducedAllergens, onUpdate, onDelete, onDuplicate, onDownloadPdf, onPublish, fishNames, mealTypes, fishLimit }) {
  const [expanded, setExpanded] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [editingMealIdx, setEditingMealIdx] = useState(null);
  const [editFocus, setEditFocus] = useState(false);
  const [focusText, setFocusText] = useState(week.focus || '');
  const [fishWarning, setFishWarning] = useState('');
  const [allergenWarnings, setAllergenWarnings] = useState([]);
  const [weekNote, setWeekNote] = useState(week.week_note || '');
  const [weekNoteVis, setWeekNoteVis] = useState(week.week_note_visibility || 'mae');
  const [mealNotes, setMealNotes] = useState(week.meal_type_notes || {});
  const [showWeekNote, setShowWeekNote] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const confirmRestoreVersion = useConfirmDelete();
  const [clinical, setClinical] = useState({ issues: [], notes: [] });
  const daysKey = JSON.stringify(week.days || {});
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/baby-menus/${week.id}/clinical-validation`)
      .then(r => { if (!cancelled) setClinical(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [week.id, week.updated_at, daysKey]);
  const issuesFor = (day, mi) => clinical.issues.filter(i => i.day === day && i.meal_index === mi);
  const dayIssues = (day) => clinical.issues.filter(i => i.day === day && i.meal_index === null);

  const restoreVersion = async (snapshot) => {
    const ok = await confirmRestoreVersion({
      description: 'Vais repor esta versão anterior da semana. O conteúdo atual é substituído.',
      confirmLabel: 'Repor esta versão',
    });
    if (!ok) return;
    await onUpdate(week.id, snapshot);
    setShowVersionHistory(false);
  };

  const days = week.days || {};
  const dayOrder = Array.isArray(week.days_ordered) && week.days_ordered.length === 7
    ? week.days_ordered.map(d => ({ day: d.day, label: d.date ? `${d.weekday_label} ${d.date_label}` : d.weekday_label }))
    : DAYS.map((d) => ({ day: d, label: DAY_LABELS[d] }));

  // Count fish meals in the week
  const countFishMeals = (daysObj) => {
    let count = 0;
    const fishSet = new Set((fishNames || []).map(n => n.toLowerCase()));
    Object.values(daysObj).forEach(dayMeals => {
      if (!Array.isArray(dayMeals)) return;
      dayMeals.forEach(meal => {
        const hasFish = (meal.items || []).some(i => fishSet.has((i.food || '').toLowerCase()));
        if (hasFish) count++;
      });
    });
    return count;
  };

  const currentFishCount = countFishMeals(days);

  const saveFocus = async () => {
    await onUpdate(week.id, { focus: focusText });
    setEditFocus(false);
  };
  const saveWeekNote = async () => {
    await onUpdate(week.id, { week_note: weekNote, week_note_visibility: weekNoteVis });
    toast.success('Nota da semana guardada');
  };
  const saveMealNote = async (mealType, note, visibility) => {
    const updated = { ...mealNotes, [mealType]: { note, visibility: visibility || 'mae' } };
    setMealNotes(updated);
    await onUpdate(week.id, { meal_type_notes: updated });
  };

  const saveMeal = async (day, mealIdx, mealData) => {
    setFishWarning('');
    setAllergenWarnings([]);
    const dayMeals = [...(days[day] || [])];
    if (mealIdx !== null && mealIdx < dayMeals.length) {
      dayMeals[mealIdx] = mealData;
    } else {
      dayMeals.push(mealData);
    }
    const newDays = { ...days, [day]: dayMeals };
    try {
      await onUpdate(week.id, { days: newDays });
      setEditingDay(null); setEditingMealIdx(null);
      // Rule 1: Check allergen maintenance (non-blocking)
      if (introducedAllergens && introducedAllergens.length > 0) {
        const allFoodsInWeek = new Set();
        Object.values(newDays).forEach(dayM => {
          if (!Array.isArray(dayM)) return;
          dayM.forEach(meal => (meal.items || []).forEach(i => { if (i.food) allFoodsInWeek.add(i.food.toLowerCase()); }));
        });
        const missing = introducedAllergens.filter(a => !allFoodsInWeek.has(a.toLowerCase()));
        if (missing.length > 0) {
          setAllergenWarnings(missing.map(a => `O alergénio "${a}" ainda não aparece esta semana. Recomenda-se exposição regular após introdução.`));
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const deleteMeal = async (day, mealIdx) => {
    const dayMeals = [...(days[day] || [])];
    dayMeals.splice(mealIdx, 1);
    const newDays = { ...days, [day]: dayMeals };
    await onUpdate(week.id, { days: newDays });
  };

  const getMealLabel = (key) => mealTypes.find(m => m.key === key)?.label || key;

  return (
    <div className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid={`baby-week-${week.id}`}>
      <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-brand-bg/50" onClick={() => setExpanded(!expanded)} data-testid={`baby-week-expand-${week.id}`}>
        <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
          <span className="font-heading font-semibold text-brand-green text-sm">{`S${week.week_number}`}</span>
        </div>
        <div className="flex-1 min-w-0">
          {editFocus ? (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <Input value={focusText} onChange={e => setFocusText(e.target.value)} className="text-sm rounded-lg border-brand-border h-8" autoFocus />
              <button onClick={saveFocus} className="text-brand-green"><FloppyDisk size={16} /></button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-brand-text truncate">{week.focus || 'Sem foco definido'}</p>
                {week.generated_by_ai && <span className="text-[8px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 bg-brand-bg text-brand-muted" title="Gerada automaticamente" data-testid={`baby-week-ai-badge-${week.id}`}>IA</span>}
                <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${clinical.issues.length ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} title="Semáforo clínico (não bloqueia)" data-testid={`baby-week-semaforo-${week.id}`}>
                  {clinical.issues.length ? `${clinical.issues.length} aviso${clinical.issues.length > 1 ? 's' : ''} clínico${clinical.issues.length > 1 ? 's' : ''}` : 'Sem avisos clínicos'}
                </span>
                <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${week.status === 'publicado' ? 'bg-brand-green/10 text-brand-green' : 'bg-amber-100 text-amber-700'}`} data-testid={`baby-week-status-${week.id}`}>
                  {week.status === 'publicado' ? 'Publicado' : 'Rascunho'}
                </span>
              </div>
              <p className="text-[10px] text-brand-muted truncate">{week.label ? `${week.label} · ` : ''}{Object.keys(days).filter(d => (days[d] || []).length > 0).length} de 7 dias preenchidos</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
          {week.status !== 'publicado' && (
            <button onClick={() => onPublish(week.id)} className="text-[10px] font-medium bg-brand-green text-white rounded-full px-3 py-1.5" data-testid={`baby-week-publish-${week.id}`}>Publicar</button>
          )}
          <button onClick={() => { setEditFocus(true); setFocusText(week.focus || ''); }} className="text-brand-muted hover:text-brand-green p-1" data-testid={`baby-week-edit-focus-${week.id}`}><PencilSimple size={14} /></button>
          <button onClick={(e) => { e.stopPropagation(); setShowWeekNote(!showWeekNote); }} className={`text-brand-muted hover:text-brand-green p-1 ${weekNote ? 'text-brand-green' : ''}`} title="Nota da semana" data-testid={`baby-week-note-${week.id}`}><NotePencil size={14} /></button>
          <button onClick={() => onDownloadPdf(week.id, week.label || `S${week.week_number}`)} className="text-brand-muted hover:text-brand-green p-1" title="PDF" data-testid={`baby-week-pdf-${week.id}`}><FilePdf size={14} /></button>
          <button onClick={() => onDuplicate(week.id)} className="text-brand-muted hover:text-brand-green p-1" title="Duplicar" data-testid={`baby-week-duplicate-${week.id}`}><Copy size={14} /></button>
          {(week.version_history || []).length > 0 && (
            <button onClick={() => setShowVersionHistory(true)} className="text-brand-muted hover:text-brand-green p-1" title="Ver versões anteriores" data-testid={`baby-week-history-${week.id}`}><ClockCounterClockwise size={14} /></button>
          )}
          <button onClick={() => onDelete(week.id)} className="text-brand-muted hover:text-red-500 p-1" data-testid={`baby-week-delete-${week.id}`}><Trash size={14} /></button>
        </div>
        {expanded ? <CaretUp size={16} className="text-brand-muted" /> : <CaretDown size={16} className="text-brand-muted" />}
      </div>

      {expanded && (
        <div className="border-t border-brand-border">
          {/* Week note */}
          {showWeekNote && (
            <div className="p-3 bg-brand-bg/20 border-b border-brand-border/50">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] text-brand-muted font-medium">Nota da semana</span>
                <select value={weekNoteVis} onChange={e => setWeekNoteVis(e.target.value)} className="text-[9px] border border-brand-border rounded-lg px-1.5 py-0.5 bg-white">
                  <option value="mae">Visível para a mae</option>
                  <option value="interna">Só para mim</option>
                </select>
              </div>
              <div className="flex gap-1.5">
                <Textarea value={weekNote} onChange={e => setWeekNote(e.target.value)} placeholder="Nota geral desta semana..." className="flex-1 rounded-lg border-brand-border text-xs" rows={2} />
                <button onClick={saveWeekNote} className="text-brand-green self-end p-1"><FloppyDisk size={16} /></button>
              </div>
            </div>
          )}
          {(clinical.issues.length > 0 || clinical.notes.length > 0) && (
            <div className="p-3 border-b border-brand-border/50 space-y-1" data-testid={`baby-week-semaforo-panel-${week.id}`}>
              {clinical.issues.length > 0 && <p className="text-[11px] font-semibold text-red-700">Semáforo clínico, avisos (não bloqueiam a gravação nem a publicação)</p>}
              {clinical.issues.map((i, k) => (
                <p key={k} className="text-[10px] text-red-700 flex gap-1.5" data-testid={`semaforo-issue-${week.id}-${k}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1 flex-shrink-0" />
                  <span>{i.day ? `${DAY_LABELS[i.day] || i.day}${i.meal_type ? ` · ${getMealLabel(i.meal_type)}` : ''}: ` : ''}{i.message}</span>
                </p>
              ))}
              {clinical.notes.map((n, k) => <p key={`n${k}`} className="text-[10px] text-brand-muted" data-testid={`semaforo-note-${week.id}-${k}`}>ℹ {n}</p>)}
            </div>
          )}
          <div className="grid grid-cols-7 text-center bg-brand-bg/50 py-1.5">
            {dayOrder.map(({ day, label }) => <span key={day} className={`text-[10px] font-semibold uppercase ${dayIssues(day).length ? 'text-red-600' : 'text-brand-muted'}`} title={dayIssues(day).map(i => i.message).join(' | ')} data-testid={`week-day-label-${day}`}>{label}{dayIssues(day).length > 0 && ' ●'}</span>)}
          </div>
          <div className="grid grid-cols-7 divide-x divide-brand-border min-h-[120px]">
            {dayOrder.map(({ day }) => {
              const meals = days[day] || [];
              return (
                <div key={day} className="p-1.5 space-y-1">
                  {meals.map((meal, mi) => (
                    <div key={mi} className={`rounded-lg p-1.5 text-[10px] group relative ${issuesFor(day, mi).length ? 'bg-red-50 ring-1 ring-red-400' : 'bg-brand-bg'}`} title={issuesFor(day, mi).map(i => i.message).join(' | ')} data-testid={`meal-${day}-${mi}`}>
                      {issuesFor(day, mi).length > 0 && <span className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-red-500" data-testid={`meal-warning-${day}-${mi}`} />}
                      <p className="font-semibold text-brand-text truncate">{getMealLabel(meal.type)}</p>
                      {(meal.items || []).map((item, ii) => (
                        <p key={ii} className="text-brand-muted truncate">{item.food}</p>
                      ))}
                      {(meal.info_lines || []).map((line, li) => (
                        <p key={`info-${li}`} className="text-[8px] text-brand-green/70 italic truncate">{line}</p>
                      ))}
                      <div className="hidden group-hover:flex absolute top-0.5 right-0.5 gap-0.5">
                        <button onClick={() => { setEditingDay(day); setEditingMealIdx(mi); }} className="text-brand-muted hover:text-brand-green"><PencilSimple size={10} /></button>
                        <button onClick={() => deleteMeal(day, mi)} className="text-brand-muted hover:text-red-500"><Trash size={10} /></button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => { setEditingDay(day); setEditingMealIdx(null); }} className="w-full text-[9px] text-brand-green hover:bg-brand-green/5 rounded py-1 flex items-center justify-center gap-0.5">
                    <Plus size={8} /> Refeição
                  </button>
                </div>
              );
            })}
          </div>

          {editingDay && (
            <div className="border-t border-brand-border p-4 bg-brand-bg/30">
              <p className="text-xs font-medium text-brand-text mb-2">
                {editingMealIdx !== null ? 'Editar' : 'Nova'} refeição: {DAY_LABELS[editingDay] || editingDay}
              </p>
              {currentFishCount > 0 && (
                <p className="text-[10px] text-amber-600 mb-2" data-testid="fish-count-info">
                  Peixe/pescado nesta semana: {currentFishCount}/{fishLimit || 4}
                </p>
              )}
              {fishWarning && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-xl mb-2 text-xs text-amber-700" data-testid="fish-limit-warning">
                  {fishWarning}
                </div>
              )}
              {allergenWarnings.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {allergenWarnings.map((w, i) => (
                    <div key={i} className="p-2 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700" data-testid="allergen-warning">
                      {w}
                    </div>
                  ))}
                </div>
              )}
              <MealEditor
                meal={editingMealIdx !== null ? (days[editingDay] || [])[editingMealIdx] : null}
                babyFoods={babyFoods}
                groupLabels={groupLabels}
                groupData={groupData}
                mealTypes={mealTypes}
                onSave={(data) => saveMeal(editingDay, editingMealIdx, data)}
                onCancel={() => { setEditingDay(null); setEditingMealIdx(null); }}
              />
            </div>
          )}
        </div>
      )}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="rounded-3xl border-brand-border max-w-lg" data-testid={`baby-week-history-modal-${week.id}`}>
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Versões anteriores da semana</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[28rem] overflow-y-auto">
            {[...(week.version_history || [])].reverse().map((entry, i) => (
              <div key={i} className="bg-brand-bg/50 rounded-xl p-3" data-testid={`baby-week-history-entry-${i}`}>
                <p className="text-xs text-brand-muted mb-1">
                  {entry.replaced_by_name ? `${entry.replaced_by_name} · ` : ''}
                  {entry.replaced_at ? fmtStamp(entry.replaced_at) : ''}
                </p>
                <p className="text-xs text-brand-text">{(entry.snapshot?.label || entry.snapshot?.focus) ? `${entry.snapshot.focus || ''} ${entry.snapshot.label ? `(${entry.snapshot.label})` : ''}`.trim() : 'Conteúdo dos dias desta semana'}</p>
                <button type="button" onClick={() => restoreVersion(entry.snapshot)} className="mt-2 text-xs text-brand-green font-medium hover:underline" data-testid={`baby-week-restore-${i}`}>Repor esta versão</button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MenuRulesGrid({ routine, fishAtSchool, editable, onChangeCell, onToggleFish }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse" data-testid="menu-rules-grid">
        <thead>
          <tr>
            <th className="text-left p-1.5 text-brand-muted font-medium">Refeição</th>
            {DAYS.map(d => <th key={d} className="p-1.5 text-brand-muted font-semibold uppercase">{DAY_LABELS[d]}</th>)}
          </tr>
        </thead>
        <tbody>
          {MENU_RULE_MEALS.map(meal => (
            <tr key={meal} className="border-t border-brand-border/50">
              <td className="p-1.5 font-medium text-brand-text whitespace-nowrap">{MENU_RULE_MEAL_LABELS[meal]}</td>
              {DAYS.map(day => (
                <td key={day} className="p-1">
                  {editable ? (
                    <select value={routine?.[day]?.[meal] || ''} onChange={e => onChangeCell(day, meal, e.target.value)}
                      className="w-full border border-brand-border rounded-lg text-[9px] px-1 py-1 bg-white" data-testid={`menu-rule-cell-${day}-${meal}`}>
                      <option value="">Escolher</option>
                      <option value="casa">Casa</option>
                      <option value="escola">Escola</option>
                      <option value="leite">Leite</option>
                    </select>
                  ) : (
                    <span className={`block text-center rounded-lg py-1 ${routine?.[day]?.[meal] ? 'bg-brand-bg text-brand-text' : 'text-brand-placeholder'}`} data-testid={`menu-rule-cell-${day}-${meal}`}>
                      {MENU_RULE_LOCATION_LABELS[routine?.[day]?.[meal]] || '\u00A0'}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-brand-border/50">
            <td className="p-1.5 font-medium text-brand-text">Peixe na escola</td>
            {DAYS.map(day => (
              <td key={day} className="p-1 text-center">
                <input type="checkbox" checked={!!fishAtSchool?.[day]} disabled={!editable} onChange={e => onToggleFish(day, e.target.checked)} data-testid={`menu-rule-fish-${day}`} />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function FoodTagPicker({ label, tags, onAdd, onRemove, foods, placeholder, testPrefix, disabled }) {
  const [query, setQuery] = useState('');
  const tagName = (t) => (typeof t === 'string' ? t : t.name);
  const tagLinked = (t) => typeof t === 'object' && t !== null && !!t.food_id;
  const alreadyAdded = new Set(tags.map(t => (typeof t === 'string' ? t.toLowerCase() : (t.food_id || t.name.toLowerCase()))));
  const matches = query.trim().length > 0
    ? foods.filter(f => f.name.toLowerCase().includes(query.trim().toLowerCase()) && !alreadyAdded.has(f.id)).slice(0, 8)
    : [];
  return (
    <div>
      <Label className="text-xs text-brand-muted mb-1 block">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {tags.length === 0 && <span className="text-[10px] text-brand-placeholder">Nenhum registado</span>}
        {tags.map((t, i) => {
          const linked = tagLinked(t);
          return (
            <span key={`${tagName(t)}-${i}`} className={`flex items-center gap-1 text-[10px] rounded-full px-2 py-1 ${linked ? 'bg-brand-bg text-brand-text' : 'bg-red-50 text-red-600 border border-red-200'}`} data-testid={`${testPrefix}-tag-${i}`} title={linked ? '' : 'Não corresponde a nenhum alimento da biblioteca, corrige'}>
              {!linked && <Warning size={9} weight="fill" />}
              {tagName(t)}
              {!disabled && <button onClick={() => onRemove(i)} className="text-brand-muted hover:text-red-500"><Trash size={9} /></button>}
            </span>
          );
        })}
      </div>
      {!disabled && (
        <div className="relative">
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} className="rounded-lg border-brand-border text-xs h-8" data-testid={`${testPrefix}-input`} />
          {matches.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-brand-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {matches.map(f => (
                <button key={f.id} type="button" onClick={() => { onAdd({ food_id: f.id, name: f.name }); setQuery(''); }}
                  className="w-full text-left text-xs px-2.5 py-1.5 hover:bg-brand-bg text-brand-text" data-testid={`${testPrefix}-option-${f.id}`}>
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FoodOrderedPicker({ label, items, onChange, foods, categories = [], placeholder, testPrefix, disabled, alreadyIntroducedMap = {} }) {
  const [query, setQuery] = useState('');
  const tagName = (t) => (typeof t === 'string' ? t : t.name);
  const isCategoryItem = (t) => typeof t === 'object' && t !== null && !t.food_id && categories.some(c => c.toLowerCase() === (t.name || '').toLowerCase());
  const tagLinked = (t) => (typeof t === 'object' && t !== null && !!t.food_id) || isCategoryItem(t);
  const alreadyAdded = new Set(items.map(t => (typeof t === 'string' ? t.toLowerCase() : (t.food_id || t.name.toLowerCase()))));
  const matches = query.trim().length > 0
    ? foods.filter(f => f.name.toLowerCase().includes(query.trim().toLowerCase()) && !alreadyAdded.has(f.id)).slice(0, 8)
    : [];
  const catMatches = categories.filter(c => (query.trim() === '' || c.toLowerCase().includes(query.trim().toLowerCase())) && !alreadyAdded.has(c.toLowerCase()));
  const add = (ref) => {
    const already = alreadyIntroducedMap[(ref.name || '').trim().toLowerCase()];
    if (already) {
      toast.warning(`Este alimento já foi introduzido na ${already}.`);
    }
    onChange([...items, ref]);
    setQuery('');
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const u = [...items];
    [u[i], u[j]] = [u[j], u[i]];
    onChange(u);
  };
  return (
    <div>
      <Label className="text-xs text-brand-muted mb-1 block">{label}</Label>
      <div className="space-y-1 mb-1.5">
        {items.length === 0 && <span className="text-[10px] text-brand-placeholder">Nenhum registado</span>}
        {items.map((t, i) => {
          const linked = tagLinked(t);
          const isCat = isCategoryItem(t);
          return (
            <div key={`${tagName(t)}-${i}`} className={`flex items-center gap-1.5 rounded-lg px-2 py-1 ${linked ? (isCat ? 'bg-brand-green/10' : 'bg-brand-bg') : 'bg-red-50 border border-red-200'}`} data-testid={`${testPrefix}-item-${i}`} title={linked ? '' : 'Não corresponde a nenhum alimento da biblioteca, corrige'}>
              <span className="text-[10px] font-semibold text-brand-green w-4">{i + 1}.</span>
              {!linked && <Warning size={9} weight="fill" className="text-red-500" />}
              <span className={`text-[10px] flex-1 ${linked ? 'text-brand-text' : 'text-red-600'}`}>{isCat ? `Tipo: ${tagName(t)}` : tagName(t)}</span>
              {!disabled && (
                <div className="flex items-center gap-0.5">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-brand-muted hover:text-brand-green disabled:opacity-30 p-0.5" data-testid={`${testPrefix}-up-${i}`}><CaretUp size={11} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-brand-muted hover:text-brand-green disabled:opacity-30 p-0.5" data-testid={`${testPrefix}-down-${i}`}><CaretDown size={11} /></button>
                  <button onClick={() => remove(i)} className="text-brand-muted hover:text-red-500 p-0.5"><Trash size={11} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!disabled && (
        <div className="relative">
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} className="rounded-lg border-brand-border text-xs h-8" data-testid={`${testPrefix}-input`} />
          {(matches.length > 0 || catMatches.length > 0) && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-brand-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {catMatches.map(c => (
                <button key={`cat-${c}`} type="button" onClick={() => add({ food_id: null, name: c })}
                  className="w-full text-left text-xs px-2.5 py-1.5 hover:bg-brand-green/10 text-brand-green font-medium" data-testid={`${testPrefix}-cat-option-${c}`}>
                  Tipo: {c}
                </button>
              ))}
              {matches.map(f => (
                <button key={f.id} type="button" onClick={() => add({ food_id: f.id, name: f.name })}
                  className="w-full text-left text-xs px-2.5 py-1.5 hover:bg-brand-bg text-brand-text" data-testid={`${testPrefix}-option-${f.id}`}>
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MealGroupOverrideRow({ mealKey, mealLabel, groupLabels, override, onChange, disabled }) {
  const required = override?.required || [];
  const optional = override?.optional || [];
  const active = !!override;
  const cycle = (g) => {
    let newRequired = [...required], newOptional = [...optional];
    if (required.includes(g)) { newRequired = required.filter(x => x !== g); newOptional = [...optional, g]; }
    else if (optional.includes(g)) { newOptional = optional.filter(x => x !== g); }
    else { newRequired = [...required, g]; }
    onChange({ required: newRequired, optional: newOptional });
  };
  return (
    <div className="border border-brand-border rounded-xl p-2.5" data-testid={`meal-override-row-${mealKey}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-brand-text">{mealLabel}</span>
        <button type="button" disabled={disabled} onClick={() => onChange(active ? null : { required: [], optional: [] })}
          className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${active ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}
          data-testid={`meal-override-toggle-${mealKey}`}>
          {active ? 'Regra própria ativa' : 'Usar regra global'}
        </button>
      </div>
      {active && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(groupLabels).map(([g, label]) => {
            const state = required.includes(g) ? 'required' : optional.includes(g) ? 'optional' : 'none';
            const cls = state === 'required' ? 'bg-brand-green text-white' : state === 'optional' ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-white text-brand-muted border border-brand-border';
            return (
              <button key={g} type="button" disabled={disabled} onClick={() => cycle(g)} className={`text-[10px] px-2 py-1 rounded-full ${cls}`} data-testid={`meal-override-group-${mealKey}-${g}`}>
                {label}{state === 'required' ? ' (obrig.)' : state === 'optional' ? ' (opc.)' : ''}
              </button>
            );
          })}
          <p className="text-[9px] text-brand-placeholder w-full mt-0.5">Clica num grupo para alternar: nenhum → obrigatório → opcional → nenhum.</p>
        </div>
      )}
    </div>
  );
}

function MenuRulesSection({ patientId, outOfOrderWarnings = [], horticolaRepeatWarnings = [], alreadyIntroducedMap = {} }) {
  const [data, setData] = useState({ active: null, draft: null, suggestions: { introduced_foods: [], allergy_suggestions: [] } });
  const [loading, setLoading] = useState(true);
  const [foods, setFoods] = useState([]);
  const [allergenCategories, setAllergenCategories] = useState([]);
  const [groupLabelsForOverride, setGroupLabelsForOverride] = useState({});
  const [editRoutine, setEditRoutine] = useState(BLANK_ROUTINE());
  const [editFish, setEditFish] = useState(BLANK_FISH());
  const [editIntro, setEditIntro] = useState([]);
  const [editAllergies, setEditAllergies] = useState([]);
  const [editAllergenOrder, setEditAllergenOrder] = useState([]);
  const [editMealOverrides, setEditMealOverrides] = useState({});
  const [editNotes, setEditNotes] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readiness, setReadiness] = useState({ ready: false, missing: [] });
  const [showGenerate, setShowGenerate] = useState(false);
  const [genWeeks, setGenWeeks] = useState(1);
  const [genStartDate, setGenStartDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genConflicts, setGenConflicts] = useState(null);
  const [genResult, setGenResult] = useState(null);

  const fetchReadiness = useCallback(async () => {
    try {
      const { data: r } = await axios.get(`${API}/api/patients/${patientId}/menu-rules/generation-readiness`);
      setReadiness(r);
    } catch {}
  }, [patientId]);
  useEffect(() => { fetchReadiness(); }, [fetchReadiness]);

  const runGenerate = async (confirmOverlap = false) => {
    setGenerating(true);
    try {
      const { data: res } = await axios.post(`${API}/api/patients/${patientId}/baby-menus/generate-ai`, {
        num_weeks: genWeeks, start_date: genStartDate, confirm_overlap: confirmOverlap,
      });
      setGenConflicts(null);
      setShowGenerate(false);
      setGenResult(res);
    } catch (e) {
      if (e.response?.status === 409) {
        setGenConflicts(e.response.data.detail);
      } else {
        toast.error(e.response?.data?.detail || 'Erro ao gerar ementa');
      }
    } finally { setGenerating(false); }
  };

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/patients/${patientId}/menu-rules`);
      setData(d);
      const source = d.draft || d.active;
      setEditRoutine(source?.weekly_routine || BLANK_ROUTINE());
      setEditFish(source?.fish_at_school || BLANK_FISH());
      setEditIntro(source?.introduced_foods || []);
      setEditAllergies(source?.allergies_to_avoid || []);
      setEditAllergenOrder(source?.allergens_to_introduce_order || []);
      setEditMealOverrides(source?.meal_group_overrides || {});
      setEditNotes(source?.notes || '');
    } catch {} finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    axios.get(`${API}/api/baby-foods`).then(({ data: f }) => setFoods(f)).catch(() => {});
    axios.get(`${API}/api/baby-food-groups`).then(({ data: g }) => {
      const gl = {};
      (Array.isArray(g) ? g : []).forEach(x => { gl[x.key] = x.label; });
      setGroupLabelsForOverride(gl);
    }).catch(() => {});
    axios.get(`${API}/api/baby-allergen-categories`).then(({ data: c }) => setAllergenCategories(c)).catch(() => {});
  }, []);

  const startDraft = async () => {
    try {
      await axios.post(`${API}/api/patients/${patientId}/menu-rules/draft`);
      toast.success('Rascunho criado');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar rascunho'); }
  };

  const [showStaleWarning, setShowStaleWarning] = useState(false);
  const [approving, setApproving] = useState(false);

  const persistDraft = async () => {
    const { data: saved } = await axios.put(`${API}/api/patients/${patientId}/menu-rules/draft`, {
      weekly_routine: editRoutine, fish_at_school: editFish,
      introduced_foods: editIntro, allergies_to_avoid: editAllergies,
      allergens_to_introduce_order: editAllergenOrder, meal_group_overrides: editMealOverrides, notes: editNotes,
      expected_updated_at: data.draft?.updated_at,
    });
    return saved;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await persistDraft();
      toast.success('Rascunho guardado');
      fetchData();
    } catch (e) {
      if (e.response?.status === 409) { setShowStaleWarning(true); } else { toast.error('Erro ao guardar'); }
    } finally { setSaving(false); }
  };

  const approveDraft = async () => {
    setApproving(true);
    try {
      const saved = await persistDraft();
      await axios.post(`${API}/api/patients/${patientId}/menu-rules/draft/approve`, { expected_updated_at: saved.updated_at });
      toast.success('Regras guardadas, aprovadas e ativas');
      fetchData();
    } catch (e) {
      if (e.response?.status === 409) { setShowStaleWarning(true); } else { toast.error(e.response?.data?.detail || 'Erro ao aprovar'); }
    } finally { setApproving(false); }
  };

  const discardDraft = async () => {
    try {
      await axios.delete(`${API}/api/patients/${patientId}/menu-rules/draft`);
      toast.success('Rascunho descartado');
      setConfirmDiscard(false);
      fetchData();
    } catch { toast.error('Erro ao descartar'); }
  };

  const openHistory = async () => {
    try {
      const { data: h } = await axios.get(`${API}/api/patients/${patientId}/menu-rules/history`);
      setHistory(h);
      setShowHistory(true);
    } catch { toast.error('Erro ao carregar histórico'); }
  };

  const changeCell = (day, meal, val) => setEditRoutine(prev => ({ ...prev, [day]: { ...prev[day], [meal]: val } }));
  const toggleFish = (day, val) => setEditFish(prev => ({ ...prev, [day]: val }));

  if (loading) return null;
  const hasDraft = !!data.draft;
  const hasActive = !!data.active;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="menu-rules-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-medium text-sm text-brand-text">Regras da Ementa</h3>
          <p className="text-[10px] text-brand-muted mt-0.5">Rotina semanal, alimentos introduzidos e alergias, usados para gerar as ementas.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openHistory} className="text-[10px] text-brand-muted hover:text-brand-green border border-brand-border rounded-full px-3 py-1.5" data-testid="menu-rules-history-btn">Histórico</button>
          <button
            onClick={() => { setGenStartDate(''); setGenWeeks(1); setShowGenerate(true); }}
            disabled={!readiness.ready}
            title={readiness.ready ? '' : readiness.missing.join(' · ')}
            className={`text-[10px] font-medium rounded-full px-3 py-1.5 flex items-center gap-1.5 ${readiness.ready ? 'bg-brand-text text-white' : 'bg-brand-bg text-brand-placeholder cursor-not-allowed'}`}
            data-testid="menu-rules-generate-btn">
            <span className={`w-1.5 h-1.5 rounded-full ${readiness.ready ? 'bg-green-400' : 'bg-amber-400'}`} data-testid="menu-rules-readiness-dot" />
            Gerar ementa
          </button>
          {!hasDraft && (
            <button onClick={startDraft} className="text-[10px] font-medium bg-brand-green text-white rounded-full px-3 py-1.5" data-testid="menu-rules-create-draft-btn">
              {hasActive ? 'Editar (novo rascunho)' : 'Criar regras'}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${hasActive ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted'}`} data-testid="menu-rules-active-badge">
          {hasActive ? 'Versão ativa aprovada' : 'Sem versão aprovada'}
        </span>
        {hasDraft && <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="menu-rules-draft-badge">Rascunho pendente de aprovação</span>}
      </div>

      {!readiness.ready && readiness.missing.length > 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-2.5" data-testid="menu-rules-readiness-missing">
          <p className="text-[9px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Falta para poder gerar ementas</p>
          <ul className="space-y-0.5">
            {readiness.missing.map((m, i) => <li key={i} className="text-[10px] text-amber-800">• {m}</li>)}
          </ul>
        </div>
      )}

      {!hasActive && !hasDraft ? (
        <p className="text-xs text-brand-placeholder">Ainda não há regras definidas para esta paciente.</p>
      ) : (
        <>
          <MenuRulesGrid routine={editRoutine} fishAtSchool={editFish} editable={hasDraft} onChangeCell={changeCell} onToggleFish={toggleFish} />

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <FoodTagPicker label="Alimentos já introduzidos" tags={editIntro} foods={foods}
              onAdd={(ref) => setEditIntro(prev => [...prev, ref])}
              onRemove={(i) => setEditIntro(prev => prev.filter((_, idx) => idx !== i))}
              placeholder="Pesquisar alimento na biblioteca..." testPrefix="menu-rules-intro" disabled={!hasDraft} />
            <FoodTagPicker label="Alergias / alimentos a evitar" tags={editAllergies} foods={foods}
              onAdd={(ref) => setEditAllergies(prev => [...prev, ref])}
              onRemove={(i) => setEditAllergies(prev => prev.filter((_, idx) => idx !== i))}
              placeholder="Pesquisar alimento na biblioteca..." testPrefix="menu-rules-allergy" disabled={!hasDraft} />
          </div>

          {hasDraft && data.suggestions?.allergy_suggestions?.length > 0 && (
            <div className="mt-3">
              <Label className="text-[10px] text-brand-muted mb-1 block">Sugestões (registadas em introdução alimentar), clicar para confirmar</Label>
              <div className="flex flex-wrap gap-1.5">
                {data.suggestions.allergy_suggestions
                  .filter(s => !editAllergies.some(t => (typeof t === 'string' ? t : t.name).toLowerCase() === s.toLowerCase()))
                  .map(s => (
                    <button key={s} onClick={() => {
                      const match = foods.find(f => f.name.toLowerCase() === s.toLowerCase());
                      if (match) { setEditAllergies(prev => [...prev, { food_id: match.id, name: match.name }]); }
                      else { toast.error(`"${s}" não corresponde a nenhum alimento da biblioteca, escolhe manualmente no campo acima.`); }
                    }} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-1" data-testid={`menu-rules-allergy-suggestion-${s}`}>
                      + {s}
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <FoodOrderedPicker label="Alergénios a introduzir e ordem" items={editAllergenOrder} foods={foods} categories={allergenCategories}
              onChange={setEditAllergenOrder} alreadyIntroducedMap={alreadyIntroducedMap}
              placeholder="Pesquisar alimento ou tipo de alergénio..." testPrefix="menu-rules-allergen-order" disabled={!hasDraft} />
          </div>

          <div className="mt-4" data-testid="meal-group-overrides-section">
            <Label className="text-xs text-brand-muted mb-1 block">Regras de refeição específicas desta paciente</Label>
            <p className="text-[10px] text-brand-placeholder mb-2">Por defeito usa os grupos obrigatórios/opcionais globais (Admin &gt; Ementa Bebé). Ativa uma regra própria só para substituir numa refeição específica desta paciente (ex.: leguminosa em vez de batata ao jantar).</p>
            <div className="space-y-1.5">
              {MENU_RULE_MEALS.map(m => (
                <MealGroupOverrideRow key={m} mealKey={m} mealLabel={MENU_RULE_MEAL_LABELS[m]} groupLabels={groupLabelsForOverride}
                  override={editMealOverrides[m] || null} disabled={!hasDraft}
                  onChange={(val) => setEditMealOverrides(prev => {
                    const next = { ...prev };
                    if (val === null) delete next[m]; else next[m] = val;
                    return next;
                  })} />
              ))}
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-xs text-brand-muted mb-1 block">Notas</Label>
            <Textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} disabled={!hasDraft} rows={2} className="rounded-xl border-brand-border text-sm" data-testid="menu-rules-notes" />
          </div>

          {hasDraft && (
            <div className="flex gap-2 mt-4">
              <button onClick={saveDraft} disabled={saving || approving} className="text-xs border border-brand-border text-brand-muted rounded-full px-4 py-2 hover:bg-brand-bg disabled:opacity-50" data-testid="menu-rules-save-draft-btn">
                {saving ? 'A guardar...' : 'Guardar rascunho'}
              </button>
              <button onClick={approveDraft} disabled={saving || approving} className="text-xs bg-brand-green text-white rounded-full px-4 py-2 disabled:opacity-50" data-testid="menu-rules-approve-btn">
                {approving ? 'A guardar e aprovar...' : 'Aprovar rascunho'}
              </button>
              <button onClick={() => setConfirmDiscard(true)} className="text-xs text-red-500 border border-red-200 rounded-full px-4 py-2 hover:bg-red-50" data-testid="menu-rules-discard-btn">Descartar rascunho</button>
            </div>
          )}
        </>
      )}

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto" data-testid="menu-rules-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Histórico de versões</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Versões anteriores das regras da ementa, já substituídas por uma aprovação mais recente.</DialogDescription>
          </DialogHeader>
          {history.length === 0 ? (
            <p className="text-xs text-brand-placeholder">Sem versões anteriores.</p>
          ) : (
            <div className="space-y-2">
              {history.map(h => {
                const tagName = (t) => (typeof t === 'string' ? t : t?.name || '');
                const introduced = h.introduced_foods || [];
                const allergies = h.allergies_to_avoid || [];
                const allergenOrder = h.allergens_to_introduce_order || [];
                const routine = h.weekly_routine || {};
                return (
                  <div key={h.id} className="border border-brand-border rounded-xl p-3" data-testid={`menu-rules-history-item-${h.id}`}>
                    <p className="text-xs font-medium text-brand-text">Aprovada em {h.approved_at ? fmtDay(h.approved_at) : '-'}</p>
                    <p className="text-[10px] text-brand-muted">Arquivada em {h.archived_at ? fmtDay(h.archived_at) : '-'}</p>
                    {h.notes && <p className="text-[10px] text-brand-muted mt-1">Notas: {h.notes}</p>}

                    <div className="grid sm:grid-cols-3 gap-2 mt-2">
                      <div>
                        <p className="text-[9px] font-semibold text-brand-muted uppercase">Já introduzidos</p>
                        {introduced.length === 0 ? <p className="text-[10px] text-brand-placeholder">Nenhum</p> : (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {introduced.map((t, i) => <span key={i} className="text-[9px] bg-brand-bg rounded-full px-1.5 py-0.5">{tagName(t)}</span>)}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-[9px] font-semibold text-brand-muted uppercase">Alergias a evitar</p>
                        {allergies.length === 0 ? <p className="text-[10px] text-brand-placeholder">Nenhuma</p> : (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {allergies.map((t, i) => <span key={i} className="text-[9px] bg-red-50 text-red-600 rounded-full px-1.5 py-0.5">{tagName(t)}</span>)}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-[9px] font-semibold text-brand-muted uppercase">Alergénios a introduzir</p>
                        {allergenOrder.length === 0 ? <p className="text-[10px] text-brand-placeholder">Nenhum</p> : (
                          <ol className="text-[9px] text-brand-text mt-0.5 list-decimal list-inside">
                            {allergenOrder.map((t, i) => <li key={i}>{tagName(t)}</li>)}
                          </ol>
                        )}
                      </div>
                    </div>

                    <p className="text-[9px] font-semibold text-brand-muted uppercase mt-2">Grelha da rotina</p>
                    <div className="overflow-x-auto mt-0.5">
                      <table className="text-[9px] border-collapse w-full">
                        <thead>
                          <tr>
                            <th className="text-left text-brand-muted pr-1"></th>
                            {DAYS.map(day => <th key={day} className="text-brand-muted px-1 font-medium">{DAY_LABELS[day]}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {MENU_RULE_MEALS.map(meal => (
                            <tr key={meal}>
                              <td className="text-brand-muted pr-1 whitespace-nowrap">{MENU_RULE_MEAL_LABELS[meal]}</td>
                              {DAYS.map(day => (
                                <td key={day} className="text-center px-1 text-brand-text">
                                  {MENU_RULE_LOCATION_LABELS[routine?.[day]?.[meal]] || '\u00A0'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="menu-rules-discard-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Descartar rascunho?</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Esta ação não pode ser desfeita. O rascunho será eliminado e a versão aprovada ativa mantém-se inalterada.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <button onClick={discardDraft} className="flex-1 bg-red-500 text-white rounded-full py-2.5 text-sm font-medium" data-testid="menu-rules-discard-confirm-btn">Sim, descartar</button>
            <button onClick={() => setConfirmDiscard(false)} className="px-4 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Cancelar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stale data warning (edited on another device) */}
      <Dialog open={showStaleWarning} onOpenChange={setShowStaleWarning}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="menu-rules-stale-warning-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Alterado noutro dispositivo</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Este rascunho foi alterado noutro dispositivo/separador desde que abriste esta página. As tuas alterações locais ainda não foram guardadas.</DialogDescription>
          </DialogHeader>
          <button onClick={() => { setShowStaleWarning(false); fetchData(); }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="menu-rules-stale-reload-btn">Recarregar versão mais recente</button>
        </DialogContent>
      </Dialog>


      {/* Generate menu dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="menu-rules-generate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Gerar ementa</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Cria semanas em rascunho a partir das Regras da Ementa aprovadas. Nunca publica automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className={`rounded-xl px-3 py-2 text-xs flex items-center gap-2 ${(outOfOrderWarnings.length > 0 || horticolaRepeatWarnings.length > 0) ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-brand-green/5 text-brand-green border border-brand-green/20'}`} data-testid="generate-dialog-semaphore">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${(outOfOrderWarnings.length > 0 || horticolaRepeatWarnings.length > 0) ? 'bg-red-500' : 'bg-brand-green'}`} />
              {(outOfOrderWarnings.length > 0 || horticolaRepeatWarnings.length > 0)
                ? [
                    outOfOrderWarnings.length > 0 ? `${outOfOrderWarnings.length} alergénio(s) com exposições fora de ordem cronológica` : null,
                    horticolaRepeatWarnings.length > 0 ? `${horticolaRepeatWarnings.length} hortícola(s) repetido(s) em dias seguidos` : null,
                  ].filter(Boolean).join(' · ') + ' numa ementa existente. Revê antes de continuar (não regenera automaticamente).'
                : 'Sem avisos de regras nas ementas existentes.'}
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Número de semanas</Label>
              <select value={genWeeks} onChange={e => setGenWeeks(parseInt(e.target.value, 10))} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="generate-num-weeks-select">
                {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} semana{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Data de início</Label>
              <Input type="date" value={genStartDate} onChange={e => setGenStartDate(e.target.value)} className="rounded-xl border-brand-border text-sm" data-testid="generate-start-date-input" />
            </div>
          </div>
          <button onClick={() => runGenerate(false)} disabled={!genStartDate || generating} className="w-full mt-3 bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="generate-submit-btn">
            {generating ? 'A gerar...' : 'Gerar'}
          </button>
        </DialogContent>
      </Dialog>

      {/* Conflict dialog */}
      <Dialog open={!!genConflicts} onOpenChange={() => setGenConflicts(null)}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="menu-rules-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Já existem rascunhos para este período</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">{genConflicts?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 mb-2">
            {(genConflicts?.conflicts || []).map(c => (
              <div key={c.id} className="text-xs bg-brand-bg rounded-lg px-3 py-2" data-testid={`conflict-item-${c.id}`}>{c.label}, {c.start_date?.slice(0, 10)}</div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => runGenerate(true)} disabled={generating} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-50" data-testid="generate-confirm-overlap-btn">
              {generating ? 'A gerar...' : 'Continuar mesmo assim'}
            </button>
            <button onClick={() => setGenConflicts(null)} className="px-4 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Cancelar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generation result dialog */}
      <Dialog open={!!genResult} onOpenChange={() => setGenResult(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto" data-testid="menu-rules-generate-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Ementa gerada em rascunho</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Revê o resumo antes de publicar as semanas na tab de ementas.</DialogDescription>
          </DialogHeader>
          {genResult && (
            <div className="space-y-3">
              <div>
                <p className="text-[9px] font-semibold text-brand-muted uppercase tracking-wider mb-1">Semanas criadas</p>
                {genResult.weeks.map(w => (
                  <div key={w.id} className="text-xs bg-brand-bg rounded-lg px-3 py-2 mb-1" data-testid={`generated-week-${w.id}`}>{w.label} ({w.start_date?.slice(0, 10)})</div>
                ))}
              </div>
              {genResult.generation_meta.warnings.length > 0 && (
                <div>
                  <p className="text-[9px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Avisos</p>
                  {genResult.generation_meta.warnings.map((w, i) => <p key={i} className="text-[10px] text-amber-800 mb-0.5" data-testid={`generate-warning-${i}`}>• {w}</p>)}
                </div>
              )}
              {(genResult.generation_meta.notes || []).length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="generate-notes">
                  <p className="text-[11px] font-semibold text-gray-600 mb-1">Notas (não são erros)</p>
                  {genResult.generation_meta.notes.map((n, i) => <p key={i} className="text-[10px] text-gray-600 mb-0.5" data-testid={`generate-note-${i}`}>• {n}</p>)}
                </div>
              )}
              {genResult.generation_meta.proposed_introductions?.length > 0 && (
                <div>
                  <p className="text-[9px] font-semibold text-brand-green uppercase tracking-wider mb-1">Introduções propostas (visível só para ti, confirma na ficha antes de publicar)</p>
                  {genResult.generation_meta.proposed_introductions.map((pi, i) => (
                    <p key={i} className="text-[10px] text-brand-text mb-0.5" data-testid={`proposed-introduction-${i}`}>
                      • {pi.week_label} · {pi.date?.slice(0, 10)} · {pi.food} ({pi.category}) · {pi.exposure_number}ª exposição
                    </p>
                  ))}
                </div>
              )}
              {(genResult.generation_meta.missing_season_foods.length > 0 || genResult.generation_meta.excluded_min_age.length > 0) && (
                <div>
                  <p className="text-[9px] font-semibold text-red-700 uppercase tracking-wider mb-1">Alimentos excluídos</p>
                  {genResult.generation_meta.missing_season_foods.map(name => (
                    <p key={`s-${name}`} className="text-[10px] text-red-700 mb-0.5" data-testid={`excluded-season-${name}`}>• {name} (sem época definida)</p>
                  ))}
                  {genResult.generation_meta.excluded_min_age.map(f => (
                    <p key={`a-${f.name}`} className="text-[10px] text-red-700 mb-0.5" data-testid={`excluded-min-age-${f.name}`}>• {f.name} (idade mínima {f.min_age}m)</p>
                  ))}
                  <a href="/admin/alimentos-bebes" target="_blank" rel="noreferrer" className="text-[10px] text-brand-green underline" data-testid="excluded-foods-library-link">Corrigir na Biblioteca de Alimentos →</a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default function BabyMenuTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [weeks, setWeeks] = useState([]);
  const [babyFoods, setBabyFoods] = useState([]);
  const [groupLabels, setGroupLabels] = useState({});
  const [groupData, setGroupData] = useState({});
  const [introducedAllergens, setIntroducedAllergens] = useState([]);
  const [fishNames, setFishNames] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newFocus, setNewFocus] = useState('');
  const [loading, setLoading] = useState(true);
  const [seasonal, setSeasonal] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [mealTypes, setMealTypes] = useState(DEFAULT_MEAL_TYPES);
  const [showMealTypeEditor, setShowMealTypeEditor] = useState(false);
  const [editMealTypes, setEditMealTypes] = useState([]);
  const [phaseInfo, setPhaseInfo] = useState(null);
  const [phaseOverrideOpen, setPhaseOverrideOpen] = useState(false);
  const [fishLimit, setFishLimit] = useState(4);

  const fetch = useCallback(async () => {
    try {
      const [wRes, fRes, gRes, sRes, fnRes, introRes, mtRes, phRes, arRes] = await Promise.all([
        axios.get(`${API}/api/patients/${patientId}/baby-menus`),
        axios.get(`${API}/api/baby-foods`),
        axios.get(`${API}/api/baby-food-groups`),
        axios.get(`${API}/api/patients/${patientId}/seasonal-suggestions`),
        axios.get(`${API}/api/baby-foods/fish-names`),
        axios.get(`${API}/api/patients/${patientId}/food-introductions`),
        axios.get(`${API}/api/config/baby-meal-types`),
        axios.get(`${API}/api/patients/${patientId}/menu-phase`),
        axios.get(`${API}/api/admin/baby-menu-ai-rules`),
      ]);
      setWeeks(wRes.data);
      setBabyFoods(fRes.data);
      setFishNames(fnRes.data || []);
      setFishLimit(arRes.data?.fish_weekly_limit || 4);
      const gl = {};
      const gd = {};
      (Array.isArray(gRes.data) ? gRes.data : []).forEach(g => { gl[g.key] = g.label; gd[g.key] = g; });
      setGroupLabels(gl);
      setGroupData(gd);
      setSeasonal(sRes.data);
      setMealTypes(mtRes.data?.length ? mtRes.data : DEFAULT_MEAL_TYPES);
      setPhaseInfo(phRes.data);
      // Extract introduced allergens
      const intros = introRes.data || [];
      const allergenFoods = fRes.data.filter(f => f.is_allergen);
      const introducedAllergens = allergenFoods.filter(af => intros.some(i => i.food_name === af.name)).map(af => af.name);
      setIntroducedAllergens(introducedAllergens);
    } catch {} finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);

  const generateSeasonal = async () => {
    setGenLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/patients/${patientId}/seasonal-suggestions/generate`);
      setSeasonal(data);
      toast.success('Sugestões geradas!');
    } catch { toast.error('Erro ao gerar sugestões'); }
    finally { setGenLoading(false); }
  };

  const togglePublish = async () => {
    try {
      const { data } = await axios.put(`${API}/api/patients/${patientId}/seasonal-suggestions`, { published: !seasonal?.published });
      setSeasonal(data);
      toast.success(data.published ? 'Publicado para os pais' : 'Ocultado dos pais');
    } catch { toast.error('Erro'); }
  };

  const downloadPdf = async (weekId, label) => {
    try {
      const r = await axios.get(`${API}/api/baby-menus/${weekId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `ementa_bebe_${label.replace(/ /g, '_')}.pdf`; a.click();
      toast.success('PDF gerado');
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const createWeek = async () => {
    try {
      await axios.post(`${API}/api/patients/${patientId}/baby-menus`, { focus: newFocus });
      toast.success('Semana criada');
      setShowCreate(false); setNewFocus('');
      fetch();
    } catch { toast.error('Erro ao criar semana'); }
  };

  const updateWeek = async (weekId, data) => {
    try {
      const { data: result } = await axios.put(`${API}/api/baby-menus/${weekId}`, data);
      if (result._warnings && result._warnings.length > 0) {
        result._warnings.forEach(w => toast.warning(w));
      }
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const deleteWeek = async (weekId) => {
    const w = weeks.find(x => x.id === weekId);
    const ok = await confirmDelete({ description: `Vai apagar a semana ${w?.week_number != null ? `${w.week_number} ` : ''}do plano de introdução alimentar.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/baby-menus/${weekId}`); toast.success('Semana eliminada'); fetch(); } catch { toast.error('Erro'); }
  };

  const duplicateWeek = async (weekId) => {
    try { await axios.post(`${API}/api/baby-menus/${weekId}/duplicate`); toast.success('Semana duplicada'); fetch(); } catch { toast.error('Erro'); }
  };

  const publishWeek = async (weekId) => {
    try { await axios.put(`${API}/api/baby-menus/${weekId}/publish`); toast.success('Publicado para a paciente'); fetch(); } catch { toast.error('Erro ao publicar'); }
  };

  const setPhaseOverride = async (overrideKey) => {
    try {
      const { data } = await axios.put(`${API}/api/patients/${patientId}/menu-phase`, { override: overrideKey });
      setPhaseInfo(data);
      setPhaseOverrideOpen(false);
      toast.success(overrideKey ? 'Fase alterada manualmente' : 'Fase voltou ao automático');
    } catch { toast.error('Erro ao alterar fase'); }
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  const outOfOrderWarnings = checkAllergenExposureOrder(weeks);
  const horticolaRepeatWarnings = checkHorticolaRepeatViolations(weeks, babyFoods);
  const alreadyIntroducedMap = getAlreadyIntroducedMap(weeks);

  return (
    <div data-testid="baby-menu-tab">
      {outOfOrderWarnings.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 mb-5 text-xs text-red-700" data-testid="allergen-order-warning-banner">
          <p className="font-medium mb-1">Exposições de alergénios fora de ordem cronológica</p>
          {outOfOrderWarnings.map((w, i) => (
            <p key={i}>{w.food}: ordem das exposições não é crescente ({w.weeks.join(', ')}). Revê à mão.</p>
          ))}
        </div>
      )}
      {horticolaRepeatWarnings.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 mb-5 text-xs text-red-700" data-testid="horticola-repeat-warning-banner">
          <p className="font-medium mb-1">Hortícola repetido em dias seguidos</p>
          {horticolaRepeatWarnings.map((w, i) => (
            <p key={i}>{w.food} em dias seguidos ({w.dates.join(' → ')}, {w.weeks.join(', ')}). Revê à mão.</p>
          ))}
        </div>
      )}
      {/* Fase da Ementa */}
      {phaseInfo && (
        <div className="bg-white border border-brand-border rounded-2xl p-3 mb-5 flex items-center justify-between flex-wrap gap-2" data-testid="baby-menu-phase-indicator">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-brand-text">Fase da ementa: {phaseInfo.effective_phase_label}</span>
            {phaseInfo.is_override && (
              <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="baby-menu-phase-override-badge">Alterado manualmente</span>
            )}
            {phaseInfo.baby_months != null && <span className="text-[10px] text-brand-muted">({phaseInfo.baby_months} meses)</span>}
          </div>
          <div className="relative">
            <button onClick={() => setPhaseOverrideOpen(v => !v)} className="text-[10px] text-brand-green hover:underline flex items-center gap-1" data-testid="baby-menu-phase-edit-btn"><PencilSimple size={11} /> Alterar fase</button>
            {phaseOverrideOpen && (
              <div className="absolute right-0 top-6 bg-white border border-brand-border rounded-xl shadow-lg p-2 z-10 w-56">
                {BABY_MENU_PHASES.map(p => (
                  <button key={p.key} onClick={() => setPhaseOverride(p.key)} className={`w-full text-left text-[11px] px-2 py-1.5 rounded-lg hover:bg-brand-bg ${phaseInfo.effective_phase_key === p.key && phaseInfo.is_override ? 'bg-brand-green/10 text-brand-green' : ''}`} data-testid={`baby-menu-phase-option-${p.key}`}>{p.label}</button>
                ))}
                {phaseInfo.is_override && (
                  <button onClick={() => setPhaseOverride(null)} className="w-full text-left text-[11px] px-2 py-1.5 rounded-lg hover:bg-brand-bg text-brand-muted mt-1 border-t border-brand-border" data-testid="baby-menu-phase-auto-btn">Voltar ao automático ({phaseInfo.computed_phase_label})</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <MenuRulesSection patientId={patientId} outOfOrderWarnings={outOfOrderWarnings} horticolaRepeatWarnings={horticolaRepeatWarnings} alreadyIntroducedMap={alreadyIntroducedMap} />

      {/* Seasonal Suggestions Management */}
      <div className="bg-white border border-brand-gold/20 rounded-2xl p-4 mb-5" data-testid="seasonal-management">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkle size={16} className="text-brand-gold" weight="duotone" />
            <h3 className="font-heading font-medium text-sm text-brand-text">Alimentos da Época (IA)</h3>
          </div>
          <div className="flex gap-2">
            <button onClick={generateSeasonal} disabled={genLoading} className="text-xs text-brand-green border border-brand-border rounded-full px-3 py-1.5 hover:bg-brand-green/5 disabled:opacity-50" data-testid="generate-seasonal-btn">
              {genLoading ? 'A gerar...' : seasonal?.suggestions?.length ? 'Regenerar' : 'Gerar sugestões'}
            </button>
            {seasonal?.suggestions?.length > 0 && (
              <button onClick={togglePublish} className={`text-xs rounded-full px-3 py-1.5 ${seasonal?.published ? 'bg-brand-green text-white' : 'border border-brand-border text-brand-muted hover:bg-brand-bg'}`} data-testid="publish-seasonal-btn">
                {seasonal?.published ? 'Publicado' : 'Publicar'}
              </button>
            )}
          </div>
        </div>
        {seasonal?.suggestions?.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {seasonal.suggestions.map((s, i) => (
              <div key={i} className="bg-brand-bg/50 rounded-xl p-2.5">
                <p className="text-xs font-medium text-brand-text">{s.food_name}</p>
                <p className="text-[9px] text-brand-muted mt-0.5">{s.explanation}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-brand-placeholder">Gera sugestões sazonais para este bebé com IA.</p>
        )}
        {seasonal?.phase && <p className="text-[9px] text-brand-muted mt-2">Fase: {seasonal.phase} ({seasonal.baby_months} meses)</p>}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-medium text-lg text-brand-text">Ementa: Introdução alimentar</h3>
          <p className="text-xs text-brand-muted mt-0.5">Programa semanal de refeições do bebé</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditMealTypes([...mealTypes]); setShowMealTypeEditor(true); }} className="border border-brand-border text-brand-muted rounded-full px-3 py-2.5 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="edit-meal-types-btn">
            <GearSix size={14} /> Refeições
          </button>
          <button onClick={() => setShowImport(true)} className="border border-brand-border text-brand-muted rounded-full px-3 py-2.5 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="import-baby-menu-btn">
            Importar
          </button>
          <button onClick={() => setShowCreate(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="create-baby-week-btn">
          <Plus size={16} /> Nova semana
        </button>
        </div>
      </div>

      {weeks.length === 0 ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <Leaf size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">Sem semanas criadas.</p>
          <p className="text-brand-placeholder text-xs mt-1">Cria a primeira semana do programa de introdução alimentar.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {weeks.map(w => (
            <WeekView key={w.id} week={w} babyFoods={babyFoods} groupLabels={groupLabels} groupData={groupData} introducedAllergens={introducedAllergens} mealTypes={mealTypes}
              onUpdate={updateWeek} onDelete={deleteWeek} onDuplicate={duplicateWeek} onDownloadPdf={downloadPdf} onPublish={publishWeek} fishNames={fishNames} fishLimit={fishLimit} />
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova semana</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-brand-muted mb-1.5 block">Foco da semana</Label>
              <Input value={newFocus} onChange={e => setNewFocus(e.target.value)} placeholder="Ex: Introdução das leguminosas e ovo" className="rounded-xl border-brand-border" data-testid="baby-week-focus-input" />
            </div>
            <button onClick={createWeek} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="create-baby-week-submit">Criar semana</button>
          </div>
        </DialogContent>
      </Dialog>

      <ImportBabyMenuDialog open={showImport} onOpenChange={setShowImport} patientId={patientId} onImported={fetch} />

      {/* Meal Type Editor */}
      <Dialog open={showMealTypeEditor} onOpenChange={setShowMealTypeEditor}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Tipos de refeição</DialogTitle></DialogHeader>
          <p className="text-xs text-brand-muted mb-3">Edita os nomes ou adiciona novos tipos. Os dados existentes mantêm-se ligados pela chave interna. "Corresponde a" liga esta refeição à grelha de Regras da Ementa (necessário para gerar ementas).</p>
          <div className="space-y-2 mb-3">
            {editMealTypes.map((mt, i) => (
              <div key={mt.key} className="flex items-center gap-2">
                <span className="text-[9px] text-brand-placeholder font-mono w-20 truncate">{mt.key}</span>
                <Input value={mt.label} onChange={e => { const u = [...editMealTypes]; u[i] = { ...u[i], label: e.target.value }; setEditMealTypes(u); }} className="flex-1 rounded-xl border-brand-border text-sm" data-testid={`meal-type-label-${i}`} />
                <select value={mt.maps_to || ''} onChange={e => { const u = [...editMealTypes]; u[i] = { ...u[i], maps_to: e.target.value }; setEditMealTypes(u); }}
                  className="w-36 border border-brand-border rounded-lg text-[10px] px-1.5 py-1.5 bg-white" data-testid={`meal-type-maps-to-${i}`}>
                  <option value="">Corresponde a: nenhuma</option>
                  {MENU_RULE_MEALS.map(m => <option key={m} value={m}>{MENU_RULE_MEAL_LABELS[m]}</option>)}
                </select>
                {editMealTypes.length > 1 && (
                  <button onClick={() => setEditMealTypes(editMealTypes.filter((_, j) => j !== i))} className="text-brand-placeholder hover:text-red-500 p-1"><Trash size={12} /></button>
                )}
              </div>
            ))}
          </div>
          <button onClick={() => { const key = `refeicao_${Date.now()}`; setEditMealTypes([...editMealTypes, { key, label: 'Nova refeição', maps_to: '' }]); }} className="text-xs text-brand-green flex items-center gap-1 mb-3"><Plus size={12} /> Adicionar tipo</button>
          <button onClick={async () => {
            const used = editMealTypes.map(t => t.maps_to).filter(Boolean);
            const dup = used.find((v, i) => used.indexOf(v) !== i);
            if (dup) { toast.error(`Duas refeições estão marcadas como "${MENU_RULE_MEAL_LABELS[dup]}". Cada correspondência só pode ser usada 1 vez.`); return; }
            try {
              await axios.put(`${API}/api/config/baby-meal-types`, { types: editMealTypes });
              setMealTypes(editMealTypes);
              setShowMealTypeEditor(false);
              toast.success('Tipos de refeição guardados');
            } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
          }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-meal-types-btn">Guardar</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
